import React, { useEffect, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import getCentersForEventGroup from "../EventGroupAllowedCenterMap";
import Centre from "../models/Centre";
import Axios, { AxiosRequestConfig } from "axios";
import { apiBaseUrl } from "../AppSettings";

interface AnnakutRegistration {
    id: string;
    donorName: string;
    email: string;
    mobile: string;
    center: string;
    streetAddress: string;
    suburb: string;
    zip?: number;
    referenceKaryakar?: string;
    singleThalSevaCount: number;
    khandThalSevaCount: number;
    sampurnThalSevaCount: number;
    prasadCollected: boolean;
    comments?: string;
}

interface AnnakutRegistrationRendererProps {
    registration: AnnakutRegistration;
    authToken: string;
}

function AnnakutRegistrationRenderer({ registration, authToken }: AnnakutRegistrationRendererProps) {

    const [prasadCollected, setPrasadCollected] = useState<boolean>(registration.prasadCollected);
    const [comments, setComments] = useState<string>(registration.comments || "");
    const [saveInProgress, setSaveInProgress] = useState<boolean>(false);

    const editRegistration = async () => {
        const requestConfig: AxiosRequestConfig = {};
        requestConfig.headers = { 'Authorization': `Bearer ${authToken}` };
        setSaveInProgress(true);
        const commentsWithTimestamp = comments + ` (${new Date().toLocaleString()})`;
        const saveRequest = { registrationId: registration.id, comments: commentsWithTimestamp };

        try {
            const response = await Axios.post(`${apiBaseUrl}/api/AnnakutManualRegistrations/prasad-collected`, saveRequest, requestConfig);
            if (response.status === 200) {
                alert("Registration updated successfully.");
            } else {
                alert(`Error occurred while saving. Please try again later.`);
            }
        } catch (error) {
            alert(`Error occurred while saving. Please try again later.`);
        }
        finally {
            setSaveInProgress(false);
        }
    }

    return (
        <div key={registration.id} style={{ border: "1px solid gray", margin: "20px", padding: "10px" }}>
            <table style={{ width: "100%", marginTop: "10px" }}>
                <tbody>
                    <tr style={{ borderBottom: "1px solid #f0f0f0"}}>
                        <td style={{ padding: "10px" }}>
                            <Form.Check type="checkbox" label={<span>&nbsp;Prasad Collected</span>} checked={prasadCollected} onChange={(e:any) => setPrasadCollected(e.target.checked)} />
                        </td>
                        <td colSpan={2} style={{ padding: "10px" }}>
                            <Form.Control placeholder="Enter comments" type="text" value={comments} onChange={(e:any) => setComments(e.target.value)} />
                        </td>
                        <td style={{ padding: "10px" }}>
                            <Button variant="primary" disabled={saveInProgress} onClick={() => editRegistration()}>Save</Button>
                            {saveInProgress && <Spinner animation="grow" />}
                        </td>
                    </tr>
                    <tr>
                        <td><strong>Donor Name:</strong><br />{registration.donorName}</td>
                        <td><strong>Email:</strong><br />{registration.email}</td>
                        <td><strong>Mobile:</strong><br />{registration.mobile}</td>
                    </tr>
                    <tr>
                        <td><strong>Center:</strong><br />{registration.center}</td>
                        <td><strong>Street Address:</strong><br />{registration.streetAddress}</td>
                        <td><strong>Suburb:</strong><br />{registration.suburb}</td>
                        <td><strong>Zip:</strong><br />{registration.zip}</td>
                    </tr>
                    <tr>
                        <td><strong>Reference Karyakar:</strong><br />{registration.referenceKaryakar}</td>
                        <td><strong>Single Thal Seva Count:</strong><br />{registration.singleThalSevaCount}</td>
                        <td><strong>Khand Thal Seva Count:</strong><br />{registration.khandThalSevaCount}</td>
                        <td><strong>Sampurn Thal Seva Count:</strong><br />{registration.sampurnThalSevaCount}</td>
                    </tr>
                    <tr>
                        <td><strong>Prasad Collected:</strong><br />{registration.prasadCollected ? "Yes" : "No"}</td>
                        <td><strong>Comments:</strong><br />{registration.comments || "No comments"}</td>
                        <td>&nbsp;</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

export default function AnnakutPrasadCollectionTracker() {

    const [authToken, setAuthToken] = useState<string | undefined>(undefined);
    const location = useLocation();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [matchingRegistrations, setMatchingRegistrations] = useState<AnnakutRegistration[]>([]);
    const [searchInProgress, setSearchInProgress] = useState<boolean>(false);
    const [searchError, setSearchError] = useState<string>("");
    const [selectedCenter, setSelectedCenter] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (!location || !location.state) {
            console.log("No state found in location");
            return;
        }

        // @ts-ignore
        const authToken = location.state.authToken;
        setAuthToken(authToken);
    }, []);

    const search = async () => {
        const requestConfig: AxiosRequestConfig = {};
        requestConfig.headers = { 'Authorization': `Bearer ${authToken}` };
        setSearchInProgress(true);
        setSearchError("");
        const searchRequest = { center: selectedCenter, searchCriteria: searchTerm };

        try {
            const response = await Axios.post<AnnakutRegistration[]>(`${apiBaseUrl}/api/AnnakutManualRegistrations/search`, searchRequest, requestConfig);
            if (response.status === 200) {
                setMatchingRegistrations(response.data);
            } else {
                setSearchError(`Error occurred while searching. Please try again later.`);
            }
        } catch (error) {
            setSearchError(`Error occurred while searching. Please try again later.`);
        }
        finally {
            setSearchInProgress(false);
        }
    }

    const renderCentreSelect = (): Array<JSX.Element> => {
        const allowedCenters = getCentersForEventGroup("diwali-annakut-2025");
        const optionsForCentres = new Array<JSX.Element>();

        allowedCenters.forEach(eachCenter => {
            const keyNumeric: number = eachCenter;

            if (isNaN(keyNumeric))
                return;

            optionsForCentres.push(<option key={`Centre-${keyNumeric}`} value={keyNumeric}>{Centre[keyNumeric]}</option>)
        });

        return optionsForCentres;
    }

    const renderSearchControls = () => {
        return (
            <div>
                <Form.Group>
                    <Form.Label>Select Centre</Form.Label>
                    <Form.Control as="select"
                        name="centre"
                        value={selectedCenter}
                        onChange={(e: any) => setSelectedCenter(Number(e.target.value))}
                        id="centreSelect">
                        <option></option>
                        {renderCentreSelect()}
                    </Form.Control>
                </Form.Group>
                <Form.Group>
                    <Form.Control size="lg"
                        type="text"
                        value={searchTerm}
                        placeholder="Search by Name, Email, Mobile"
                        onChange={(e: any) => setSearchTerm(e.target.value)} />
                </Form.Group>
                <Form.Group>
                    <Button variant="primary" size="lg" disabled={searchTerm.length < 3 || selectedCenter === undefined || searchInProgress} onClick={search}>Search</Button>
                    {searchError && <div className="text-danger mt-2">{searchError}</div>}
                    {searchInProgress && <Spinner animation="grow" />}
                </Form.Group>
            </div>
        );
    };

    const renderMatchingRegistrations = () => {
        if (matchingRegistrations.length === 0) {
            return <div>No matching registrations found.</div>;
        }

        return (
            <div>
                <h2>Matching Registrations - {matchingRegistrations.length}</h2>
                {matchingRegistrations.map(registration => (<AnnakutRegistrationRenderer key={registration.id} registration={registration} authToken={authToken!} />))}
            </div>
        );
    };

    return (<div>
        <h1>Annakut Prasad Collection Tracker</h1>
        {renderSearchControls()}
        {renderMatchingRegistrations()}
    </div>);
}